import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "next-i18next";

import { useAddBoostToPackageCart } from "@/Apollo/hooks/package-cart/useAddBoostToPackageCart";
import { useAddListingFeeToPackageCart } from "@/Apollo/hooks/package-cart/useAddListingFeeToPackageCart";
import { useClearPackageCart } from "@/Apollo/hooks/package-cart/useClearPackageCart";
import { usePackageCart } from "@/Apollo/hooks/package-cart/usePackageCart";
import { useRemoveFromPackageCart } from "@/Apollo/hooks/package-cart/useRemoveFromPackageCart";
import { PackageKind } from "@/Apollo/schema";
import { ProductUnknown } from "@/common/contracts/product.contracts";
import { useAxiomErrorReporter } from "@/common/hooks/useAxiomErrorReporter";
import { ListingFeePackage } from "@/features/create-ad-packages/contracts/create-ad-packages.contracts";
import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import addToast from "@/toast/addToast";

type UsePackageCartSelectionArgs = {
  product?: ProductUnknown;
  listingFeePackages: ListingFeePackage[];
  /**
   * Empty the cart once, when the packages step for this product opens. Only the
   * create-ad success flow wants this — it is the start of a fresh ad, so
   * leftovers must not be preselected or silently re-charged. Off by default:
   * clearing is destructive, and elsewhere (the single-ad boost modal) the cart
   * may hold items from a failed payment that the user is about to retry.
   */
  resetCartOnMount?: boolean;
  /**
   * Buy the boost at the create-ad discount. Only the create-ad success flow
   * earns it; boosting an existing ad pays the standard price. Must match the
   * price `useCreateAdBoostPackages` renders.
   */
  discountedBoost?: boolean;
};

/** Days value representing the free "No boost" default. */
export const NO_BOOST_DAYS = 0;

/**
 * Owns the success-page package selection. Selection is local state (instant
 * radio feedback) mirrored to the server package cart via the add/remove
 * mutations; the cart itself stays the source of truth for the overview + total.
 *
 * With `resetCartOnMount` the step always starts at the free options, and a
 * refresh resets to the free plans.
 */
export const usePackageCartSelection = ({
  product,
  listingFeePackages,
  resetCartOnMount = false,
  discountedBoost = false,
}: UsePackageCartSelectionArgs) => {
  const productId = product?.id;
  const { t } = useTranslation();
  const { log: logError } = useAxiomErrorReporter("usePackageCartSelection");

  const { data } = usePackageCart({ skip: !productId });
  const cart = data?.packageCart;

  const [addBoost, { loading: addingBoost }] = useAddBoostToPackageCart();
  const [addListingFee, { loading: addingListingFee }] =
    useAddListingFeeToPackageCart();
  const [removeFromCart, { loading: removing }] = useRemoveFromPackageCart();
  const [clearCart] = useClearPackageCart();

  const freeListingFeeId = useMemo(
    () => listingFeePackages.find(pkg => pkg.isFree)?.id,
    [listingFeePackages]
  );

  const [pickedListingFeeId, setPickedListingFeeId] = useState<string>();
  const [selectedBoostDays, setSelectedBoostDays] =
    useState<number>(NO_BOOST_DAYS);

  /** Until the user picks a plan, the free one is selected. */
  const selectedListingFeeId = pickedListingFeeId ?? freeListingFeeId;

  /**
   * Once per product, never on every render. `logError` re-memoizes when the
   * viewer and merchant resolve, so an unguarded effect fires again after auth
   * settles — wiping a selection the user made in between while the radio still
   * shows it picked, and sending them to an empty checkout.
   */
  const resetForProductId = useRef<string>();

  useEffect(() => {
    if (
      PROMOTIONS_ENABLED ||
      !resetCartOnMount ||
      !productId ||
      resetForProductId.current === productId
    ) {
      return;
    }

    resetForProductId.current = productId;

    clearCart().catch(ex =>
      logError(
        "clearPackageCart",
        ex instanceof Error ? ex : new Error(String(ex))
      )
    );
  }, [clearCart, logError, productId, resetCartOnMount]);

  const notifyError = useCallback(
    (component: string, ex: unknown) => {
      logError(component, ex instanceof Error ? ex : new Error(String(ex)));
      addToast(t("common:common-error"), { appearance: "error" });
    },
    [logError, t]
  );

  const hasListingItem = !!cart?.items.some(
    item => item.kind === PackageKind.ListingFee
  );
  const hasBoostItem = !!cart?.items.some(
    item => item.kind === PackageKind.Boost
  );

  const selectListingFee = useCallback(
    async (pkg: ListingFeePackage) => {
      if (!productId) {
        return;
      }

      const previous = pickedListingFeeId;
      setPickedListingFeeId(pkg.id);

      try {
        if (pkg.isFree) {
          if (hasListingItem) {
            await removeFromCart({
              variables: { kind: PackageKind.ListingFee, productId },
            });
          }
        } else {
          await addListingFee({
            variables: {
              input: {
                productId,
                listingFeePackageId: pkg.id,
                ...(PROMOTIONS_ENABLED ? { preserveOtherListings: true } : {}),
              },
            },
          });
        }
      } catch (ex) {
        setPickedListingFeeId(previous);
        notifyError("selectListingFee", ex);
      }
    },
    [
      addListingFee,
      hasListingItem,
      notifyError,
      pickedListingFeeId,
      productId,
      removeFromCart,
    ]
  );

  const selectBoost = useCallback(
    async (days: number) => {
      if (!productId) {
        return;
      }

      const previous = selectedBoostDays;
      setSelectedBoostDays(days);

      try {
        if (days === NO_BOOST_DAYS) {
          if (hasBoostItem) {
            await removeFromCart({
              variables: { kind: PackageKind.Boost, productId },
            });
          }
        } else {
          await addBoost({
            variables: {
              input: {
                productId,
                days,
                discount: discountedBoost,
                ...(PROMOTIONS_ENABLED ? { preserveOtherListings: true } : {}),
              },
            },
          });
        }
      } catch (ex) {
        setSelectedBoostDays(previous);
        notifyError("selectBoost", ex);
      }
    },
    [
      addBoost,
      discountedBoost,
      hasBoostItem,
      notifyError,
      productId,
      removeFromCart,
      selectedBoostDays,
    ]
  );

  const selectedListingFee = listingFeePackages.find(
    pkg => pkg.id === selectedListingFeeId
  );
  const hasPaidListingFee = !!selectedListingFee && !selectedListingFee.isFree;
  const hasPaidBoost = selectedBoostDays !== NO_BOOST_DAYS;

  return {
    cart,
    selectedListingFeeId,
    selectedBoostDays,
    selectListingFee,
    selectBoost,
    hasPaidSelection: hasPaidListingFee || hasPaidBoost,
    isMutating: addingBoost || addingListingFee || removing,
  };
};
